import { odataGet, odataEscape } from './dataverse';

/**
 * Read-only access to DataHub (gaqh_*) — Compose READS, never writes.
 * Stage 1 needs client + policy context for the SODAN screen.
 *
 * Lookup key (verified live 2026-06-02): brokers read a number off Acturis
 * that Acturis labels "Version Ref." — it is NOT separately surfaced as
 * "Policy Year Ref." In our DataHub both `gaqh_versionref` and
 * `gaqh_policyyearref` exist on gaqh_policy and are EQUAL for a policy with
 * no mid-term adjustment; they can diverge if a policy was adjusted mid-year.
 * So we resolve by EITHER (versionref OR policyyearref) to be robust.
 *
 * Schema notes (verified against live metadata — selecting a non-existent
 * column 400s the whole query, so these lists are deliberately exact):
 *  - gaqh_policy has NO contactref column. The client link is the lookup
 *    `_gaqh_clientid_value` (the client's GUID). We resolve the client by
 *    that GUID, then read the client's own gaqh_contactref.
 *  - gaqh_client uses `gaqh_email` (not emailaddress), has no `gaqh_name`,
 *    and business text is `gaqh_businessdescription` (not businessactivities).
 *
 * NOTE (Open Question #2, answered): gaqh_policy carries NO per-policy add-on
 * detail. Add-on take/decline is captured fresh in Compose's own
 * gaqc_addondecision rows from the library. See memory gaqh-policy-no-addon-detail.
 */

const POLICY_SELECT = [
  'gaqh_rootpolicyref',
  'gaqh_policyyearref',
  'gaqh_versionref',
  'gaqh_product',
  'gaqh_producttarget',
  'gaqh_productcategory',
  'gaqh_description',
  'gaqh_primaryinsurer',
  'gaqh_leadinsurer',
  'gaqh_netpremium',
  'gaqh_discountedpremiumipt',
  'gaqh_inceptiondate',
  'gaqh_renewaldate',
  '_gaqh_clientid_value',
].join(',');

const CLIENT_SELECT = [
  'gaqh_clientid',
  'gaqh_contactref',
  'gaqh_clientname',
  'gaqh_clienttype',
  'gaqh_firstname',
  'gaqh_surname',
  'gaqh_addressline1',
  'gaqh_city',
  'gaqh_postcode',
  'gaqh_email',
  'gaqh_businessdescription',
  'gaqh_acturisurl',
].join(',');

export type DataHubPolicy = {
  gaqh_rootpolicyref?: number;
  gaqh_policyyearref?: number;
  gaqh_versionref?: number;
  gaqh_product?: string;
  gaqh_producttarget?: string;
  gaqh_productcategory?: string;
  gaqh_description?: string;
  gaqh_primaryinsurer?: string;
  gaqh_leadinsurer?: string;
  gaqh_netpremium?: number;
  gaqh_discountedpremiumipt?: number;
  gaqh_inceptiondate?: string;
  gaqh_renewaldate?: string;
  _gaqh_clientid_value?: string;
};

export type DataHubClient = {
  gaqh_clientid?: string;
  gaqh_contactref?: number;
  gaqh_clientname?: string;
  gaqh_clienttype?: string;
  gaqh_firstname?: string;
  gaqh_surname?: string;
  gaqh_addressline1?: string;
  gaqh_city?: string;
  gaqh_postcode?: string;
  gaqh_email?: string;
  gaqh_businessdescription?: string;
  gaqh_acturisurl?: string;
};

export type PolicyContext = {
  /** The lookup ref that resolved this policy (version/policy-year ref). */
  policyYearRef: string;
  /** Client business ref (gaqh_client.gaqh_contactref), as a string. */
  contactRef: string;
  clientName: string;
  clientType: string;
  clientAddress: string;
  productType: string;
  policyDescription: string;
  insurer: string;
  premium: string;
  inceptionDate?: string;
  renewalDate?: string;
};

function formatGBP(n?: number): string {
  if (n == null || Number.isNaN(n)) return '';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}

function joinAddress(c?: DataHubClient): string {
  if (!c) return '';
  return [c.gaqh_addressline1, c.gaqh_city, c.gaqh_postcode]
    .map((p) => (p ?? '').trim())
    .filter(Boolean)
    .join(', ');
}

/**
 * Look up a policy + its client by the ref the broker pasted (version ref or
 * policy year ref) and shape into the context the SODAN engine consumes.
 * Returns null if the ref isn't a number or no policy matches.
 *
 * `ref` is matched against the Int columns unquoted, so it must be a safe
 * integer — we parse + guard before building the filter (a quoted/garbage
 * value would 400 the query).
 */
export async function fetchPolicyContext(ref: string): Promise<PolicyContext | null> {
  const n = Number.parseInt(String(ref).trim(), 10);
  if (!Number.isSafeInteger(n) || n <= 0) return null;

  const pr = await odataGet<DataHubPolicy>(
    'gaqh_policies',
    `?$filter=gaqh_versionref eq ${n} or gaqh_policyyearref eq ${n}` +
      `&$select=${POLICY_SELECT}&$top=1`
  );
  const policy = pr.value?.[0];
  if (!policy) return null;

  // Resolve the client via the policy's lookup GUID (no contactref on policy).
  let client: DataHubClient | undefined;
  const clientGuid = policy._gaqh_clientid_value;
  if (clientGuid) {
    const cr = await odataGet<DataHubClient>(
      'gaqh_clients',
      `?$filter=gaqh_clientid eq ${clientGuid}&$select=${CLIENT_SELECT}&$top=1`
    );
    client = cr.value?.[0];
  }

  const fallbackName = [client?.gaqh_firstname, client?.gaqh_surname]
    .filter(Boolean)
    .join(' ');

  return {
    policyYearRef: String(policy.gaqh_policyyearref ?? policy.gaqh_versionref ?? n),
    contactRef: client?.gaqh_contactref != null ? String(client.gaqh_contactref) : '',
    clientName: client?.gaqh_clientname || fallbackName || '(unknown client)',
    clientType: client?.gaqh_clienttype || '',
    clientAddress: joinAddress(client),
    productType:
      policy.gaqh_producttarget || policy.gaqh_product || policy.gaqh_productcategory || '',
    policyDescription: policy.gaqh_description || '',
    insurer: policy.gaqh_primaryinsurer || policy.gaqh_leadinsurer || '',
    premium: formatGBP(policy.gaqh_discountedpremiumipt ?? policy.gaqh_netpremium),
    inceptionDate: policy.gaqh_inceptiondate,
    renewalDate: policy.gaqh_renewaldate,
  };
}

/**
 * Lightweight policy search for the picker — by description or product.
 * (gaqh_policy carries no client name, so name search is a separate path.)
 */
export async function searchPolicies(term: string): Promise<
  Array<{ policyYearRef: string; productType: string; policyDescription: string }>
> {
  const t = odataEscape(term.trim());
  if (!t) return [];
  const pr = await odataGet<DataHubPolicy>(
    'gaqh_policies',
    `?$filter=contains(gaqh_description,'${t}') or contains(gaqh_producttarget,'${t}')` +
      `&$select=gaqh_policyyearref,gaqh_versionref,gaqh_description,gaqh_producttarget,gaqh_product&$top=25`
  );
  return (pr.value ?? []).map((p) => ({
    policyYearRef: String(p.gaqh_policyyearref ?? p.gaqh_versionref ?? ''),
    productType: p.gaqh_producttarget || p.gaqh_product || '',
    policyDescription: p.gaqh_description || '',
  }));
}
